import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MangaCard from '@/components/feature/MangaCard';
import { mangas, categories } from '@/mocks/manga';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'popular' | 'latest' | 'rating'>('popular');
  const currentSlug = slug || 'all';

  const currentCategory = categories.find((c) => c.slug === currentSlug) || categories[0];

  const filteredMangas = useMemo(() => {
    let result = currentSlug === 'all'
      ? [...mangas]
      : mangas.filter((m) => m.categories.includes(currentSlug));

    switch (sortBy) {
      case 'latest':
        return result.sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
      case 'rating':
        return result.sort((a, b) => b.rating - a.rating);
      case 'popular':
      default:
        return result.sort((a, b) => b.views - a.views);
    }
  }, [currentSlug, sortBy]);

  const sortOptions = [
    { value: 'popular' as const, label: '最热门' },
    { value: 'latest' as const, label: '最新' },
    { value: 'rating' as const, label: '评分最高' },
  ];

  return (
    <>
      <SEO
        title={`${currentCategory.name}韩国漫画 - 免费韩漫在线`}
        description={`汗汗漫画${currentCategory.name}分类，提供丰富的免费${currentCategory.name}类韩国漫画在线阅读。高品质韩漫资源，每日更新，流畅阅读体验。`}
        keywords={`${currentCategory.name}韩国漫画,${currentCategory.name}韩漫,免费${currentCategory.name}漫画,在线${currentCategory.name}漫画,汗汗漫画`}
        canonicalPath={`/category/${currentSlug}`}
      />
      <div className="px-4 md:px-6 pt-6">
        <div className="max-w-7xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '漫画分类', href: '/category/all' },
              { label: currentCategory.name },
            ]}
          />

          <div className="mb-6">
            <h1 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-2">
              {currentCategory.name}韩国漫画
            </h1>
            <p className="text-sm text-foreground-500">
              共 {filteredMangas.length} 部{currentCategory.name}类韩漫作品
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-6">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-1.5 text-sm rounded-full whitespace-nowrap transition-all duration-200 cursor-pointer ${
                  currentSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/70'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-3 mb-6">
            <span className="text-sm text-foreground-500 whitespace-nowrap">排序：</span>
            <div className="flex items-center gap-1">
              {sortOptions.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setSortBy(opt.value)}
                  className={`px-3 py-1.5 text-xs rounded-full whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    sortBy === opt.value
                      ? 'bg-secondary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/70'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {filteredMangas.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5 pb-12">
              {filteredMangas.map((manga) => (
                <MangaCard key={manga.id} manga={manga} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100 text-foreground-400">
                <i className="ri-book-line text-2xl"></i>
              </div>
              <p className="text-foreground-500">该分类暂无漫画</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}